<template>
    <v-container class="py-10 px-0 px-sm-5">
        <PurchaseStatus />
        <BackButton />
        <ProgressLoader v-if="loading" />
        <div v-if="!loading && !classExists" class="class-error">
            <v-img
                :src="require('@/assets/search-error.gif')"
                alt="Class does not exist"
                max-width="400px"
                min-height="400px"
            />
            <div class="class-error-text">
                {{ $t('message.classDoesNotExist') }}
            </div>
        </div>
        <v-col v-if="!loading && classExists">
            <v-row>
                <ClassIntroduction :classObject="this.classItem" />
            </v-row>

            <SuccessBookingBanner
                v-if="haveFamilyAccountBooked && isFamilyBookingType"
            />
            <SuccessGroupBookingBanner
                :subItems="userBookedSpots"
                :isSpotsHidden="hideSpots"
                :loadingData="loadButton"
                @success="reload"
                @cancelFeePaymentError="handleError"
                v-if="haveBookedSpots && isGroupBookingType"
            ></SuccessGroupBookingBanner>

            <v-divider class="tw-my-4"></v-divider>
            <div class="tw-mt-10" />
            <p class="tw-pb-4 tw-text-3xl tw-font-bold tw-text-textPrimary">
                {{ $t('message.bookThisClass') }}
            </p>

            <SelectUsers
                :key="selectUsersKey"
                :userId="this.user?.id"
                :studioId="this.studio.id"
                :registeredSubAccounts="existingSignupFamilyUsers"
                @onChangeBookingType="onChangeBookingType"
                @updateSelectedSubAccounts="updateSelectedSubAccounts"
                :availableBookingTypes="availableBookingTypes"
                v-model="bookingType"
                :disabledBookingTypes="disabledBookingTypes"
            />
            <NotLoggedInCard
                v-if="!isUserAuth"
                class="tw-mt-4"
            ></NotLoggedInCard>
            <div v-else>
                <div class="tw-flex tw-flex-col tw-gap-4 tw-mt-6">
                    <div class="tw-text-xl tw-font-bold">
                        {{ $t('message.choosePackage') }}
                    </div>
                    <div>
                        <span class="text-h5">
                            {{
                                $t('message.numCredits', {
                                    numCredits: classCredit,
                                })
                            }}
                        </span>
                        <span class="font-weight-light">
                            {{ $t('message.perSpot') }}
                        </span>
                    </div>
                </div>

                <div class="" v-if="classItem">
                    <template v-if="bookingType === 'myself'">
                        <v-divider class="tw-my-8"></v-divider>
                        <SelectPackageCard
                            :allMemberships="allMemberships"
                            :myMemberships="myMemberships"
                            @onSelectPackage="updateSelectedPackage"
                            :selectedUserPkg="selectedPackage"
                            :studioClass="classItem"
                            :user="user"
                        />
                    </template>
                    <template v-else-if="isGroupBookingType">
                        <SelectUsersSpotCountGroup
                            v-model="groupSpotCount"
                            :userBookedSpots="userBookedSpots"
                            :selectedPackage="selectedPackage"
                            :studioClass="classItem"
                        ></SelectUsersSpotCountGroup>
                        <v-divider class="tw-my-8"></v-divider>
                        <SelectGroupPackage
                            :allMemberships="allMemberships"
                            :myMemberships="myMemberships"
                            @onSelectPackage="updateSelectedPackage"
                            :selectedUserPkg="selectedPackage"
                            :studioClass="classItem"
                            :user="user"
                        />
                    </template>
                    <template v-if="isFamilyBookingType">
                        <v-divider
                            class="tw-my-8"
                            v-if="
                                selectedFamilyUsers &&
                                selectedFamilyUsers.length > 0
                            "
                        ></v-divider>
                        <div>
                            <SelectFamilyPackage
                                :subAccounts="subAccounts"
                                :mainAccount="mainAccount"
                                :key="selectFamilyPackageKey"
                                :classId="classItem.id"
                                :userId="user.id"
                                :userPkgs="myMemberships"
                                :selectedFamilyUsers="selectedFamilyUsers"
                                :studioClass="classItem"
                                @updateSelectedPackages="
                                    updateSelectedFamilyPackages
                                "
                                @onExistingSignupFamily="onExistingSignupFamily"
                            />
                        </div>
                    </template>
                </div>
            </div>

            <v-divider class="tw-my-8"></v-divider>

            <v-col cols="12" v-if="!hideSpots" class="tw-px-0 tw-pt-0">
                <ChooseSpot
                    ref="ChooseSpotRef"
                    :spots="classItem.spots"
                    :height="classItem.height"
                    :width="classItem.width"
                    @input="updateSpotNumbers"
                    :key="chooseSpotKey"
                    :maxBookings="maxBookings"
                    :familyUsers="selectedFamilyUsers"
                    :isFamilyBooking="isFamilyBookingType"
                    :bookingType="bookingType"
                />
            </v-col>
            <div>
                <v-col cols="12">
                    <v-chip
                        class="tw-mr-3 tw-mt-3"
                        color="primary"
                        v-if="numSpotsBooked > 0"
                    >
                        {{
                            $t('message.bookedNumSpots', {
                                numSpotsBooked: numSpotsBooked,
                            })
                        }}
                    </v-chip>
                    <v-chip
                        class="tw-mr-3 tw-mt-3"
                        color="secondary darken-3"
                        v-if="waitlistPosition !== null"
                    >
                        {{
                            $t('message.waitlistPosition', {
                                waitlistPosition: this.waitlistPosition,
                            })
                        }}
                    </v-chip>
                </v-col>

                <BookSpot
                    :bookingType="bookingType"
                    :selectedPackage="selectedPackage"
                    :classObject="classItem"
                    :isBookable="isBookable"
                    :reason="unbookableReason"
                    :normalMemberships="normalMemberships"
                    :recurringMemberships="recurringMemberships"
                    :myMemberships="myMemberships"
                    :maxBookings="maxBookings"
                    :isWaitlistClass="isWaitlistClass"
                    :loading="loadButton"
                    :isClassCancellable="isClassCancellable"
                    @onNumSpots="updateNumSpots"
                    @click="submit"
                    :key="bookSpotKey"
                    :spotNumbers="spotNumbers"
                    :selectedFamilyPackages="selectedFamilyPackages"
                />

                <CancelSpotOrWaitlist
                    v-if="bookingType === 'myself'"
                    :hideSpots="hideSpots"
                    :loadButton="loadButton"
                    :userBookedSpots="userBookedSpots"
                    :userWaitlist="userWaitlist"
                    :isClassCancellable="isClassCancellable"
                    :isWaitlistCancellable="isWaitlistCancellable"
                    @success="reload"
                    @cancelFeePaymentError="handleError"
                />
                <div class="error-text">{{ validationError }}</div>
            </div>

            <CheckoutWindow
                :hideSpots="hideSpots"
                ref="CheckoutWindow"
                :packageItem="selectedPackage ? selectedPackage : {}"
                :packageId="selectedPackage ? selectedPackage.id : ''"
                :classItem="classItem"
                :spotNumbers="spotNumbers"
                :numSpots="numSpots"
                :isPurchasePackage="isPurchasePackage"
                :isBookClass="!isPurchasePackage"
                :isWaitlistClass="isWaitlistClass"
                :hideButton="true"
                :isFamilyBooking="isFamilyBookingType"
                :selectedFamilyPackages="selectedFamilyPackages"
                :key="checkoutWindowKey"
                @closeDialog="reload"
                @error="handleError"
                :bookingType="bookingType"
                :signedUpUserIds="signedUpUserIds"
                :subAccounts="allAccounts"
            />
        </v-col>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import {
    AUTHENTICATION_MODULE_NAME,
    STUDIO_MODULE_NAME,
} from '@/store/moduleNames'
import { SELECT_PACKAGE } from '@/analytics/events'
import ClassIntroduction from '@/components/bookclass/ClassIntroduction.vue'
import BookSpot from '@/components/bookclass/BookSpot.vue'
import CancelSpotOrWaitlist from '@/components/cancelclass/CancelSpotOrWaitlist'
import ChooseSpot from '@/components/bookclass/ChooseSpot.vue'
import ProgressLoader from '@/components/shared/ProgressLoader'
import CheckoutWindow from '@/components/checkout/CheckoutWindow.vue'
import PurchaseStatus from '@/components/stripe/PurchaseStatus'
import BackButton from '@/components/shared/BackButton.vue'
import SelectFamilyPackage from '@/components/bookclass/SelectFamilyPackage.vue'
import { getClassByClassId } from '@/util/cloudFunctions/classesFunctions'
import {
    getNormalPackages,
    getRecurringPackages,
    getMyMemberships,
} from '@/util/cloudFunctions/packagesFunctions'
import { isPackageValidForClass } from '@/util/packages/checkPackagesValidity'
// Cancel bookings or waitlist
import {
    getUserUpcomingClasses,
    getUserWaitlist,
} from '@/util/cloudFunctions/classesFunctions'
import SelectUsers from '@/modules/subAccounts/components/SelectUsers.vue'
import SuccessBookingBanner from '@/components/bookclass/SuccessBookingBanner.vue'
import SelectPackageCard from '../components/bookclass/SelectPackageCard.vue'
import NotLoggedInCard from '../components/bookclass/NotLoggedInCard.vue'
import SelectGroupPackage from '../components/bookclass/SelectGroupPackage.vue'
import SelectUsersSpotCountGroup from '../modules/subAccounts/components/SelectUsersSpotCountGroup.vue'
import SuccessGroupBookingBanner from '../components/bookclass/SuccessGroupBookingBanner.vue'

import { getSubAccountsByUser } from '@/components/bookclass/api/getSubAccountByUserApi'
export default {
    components: {
        ClassIntroduction,
        BookSpot,
        CancelSpotOrWaitlist,
        ProgressLoader,
        ChooseSpot,
        CheckoutWindow,
        PurchaseStatus,
        BackButton,
        SelectUsers,
        SelectFamilyPackage,
        SuccessBookingBanner,
        SelectPackageCard,
        SelectGroupPackage,
        NotLoggedInCard,
        SelectUsersSpotCountGroup,
        SuccessGroupBookingBanner,
    },
    data() {
        return {
            classId: '',
            classItem: {},
            error: '',
            isSelfBookable: false,
            isFamilyBookable: false,
            familyReason: '',
            isGroupBookable: false,
            groupReason: '',
            reason: '',
            loading: true, // loading page
            loadingClass: false,
            spotNumbers: [],
            selectedPackage: null,
            isPurchasePackage: false,
            validationError: '',
            chooseSpotKey: false,
            bookSpotKey: false,
            checkoutWindowKey: false,
            normalMemberships: [],
            recurringMemberships: [],
            myMemberships: [],
            classExists: false,
            waitlistPosition: null,
            userBookedSpots: [],
            userWaitlist: null,
            loadButton: true,
            loadingUserBookedSpots: true,
            loadingUserWaitlist: true,
            bookingType: 'myself',
            selectedFamilyUsers: [],
            selectedFamilyPackages: [],
            existingSignupFamilyUsers: [],
            groupSpotCount: 0,
            selectFamilyPackageKey: false,
            selectUsersKey: false,
            subAccounts: [],
            mainAccount: null,
        }
    },
    watch: {
        $route: {
            handler(to, from) {
                if (to !== from) {
                    this.setRequiredDataWithLoading()
                }
            },
            immediate: true,
        },
        isUserAuth: {
            handler() {
                this.setRequiredData()
            },
        },
        availableBookingTypes: {
            handler(newVal) {
                if (!newVal.includes(this.bookingType)) {
                    this.bookingType = newVal[0]
                }
            },
        },
    },
    computed: {
        numSpots() {
            return this.maxBookings
        },
        disabledBookingTypes() {
            const disabledBooking = new Map()
            if (!this.isUserAuth) return disabledBooking
            if (!this.isGroupBookable) {
                disabledBooking.set('group', this.groupReason)
            }

            if (!this.isFamilyBookable) {
                disabledBooking.set('family', this.familyReason)
            }
            return disabledBooking
        },
        haveFamilyAccountBooked() {
            return this.existingSignupFamilyUsers.length ?? 0 > 0
        },
        haveBookedSpots() {
            return (this.userBookedSpots?.length ?? 0) > 0
        },
        unbookableReason() {
            switch (this.bookingType) {
                case 'family':
                    return this.familyReason
                case 'group':
                    return this.groupReason
                default:
                    return this.reason
            }
        },
        signedUpUserIds() {
            if (!this.user || !this.user.id) {
                return []
            }

            if (this.isFamilyBookingType) {
                return this.selectedFamilyUsers.map((x) => x.id)
            }

            return [this.user.id]
        },
        groupMinSpot() {
            return 1
        },
        groupMaxSpot() {
            return this.selectedPackage.numGroupBookable ?? 0
        },
        hideSpots() {
            return this.classItem.hideSpots === true
        },
        ...mapGetters({
            user: `${AUTHENTICATION_MODULE_NAME}/getUser`,
            isUserAuth: `${AUTHENTICATION_MODULE_NAME}/isUserAuth`,
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
        }),
        maxBookings() {
            let maxBookableSpot = 1
            if (
                this.classItem.isGroupBookingsEnabled &&
                this.isGroupBookingType
            ) {
                maxBookableSpot = this.groupSpotCount
            }
            if (this.isFamilyBookingType && this.selectedFamilyUsers) {
                maxBookableSpot = this.selectedFamilyUsers.length
            }

            maxBookableSpot = Math.min(maxBookableSpot, this.freeSpots.length)
            return maxBookableSpot
        },
        numSpotsBooked() {
            if (!this.user) {
                return 0
            }

            return this.userBookedSpots.filter(
                (x) =>
                    x.userId === this.user.id ||
                    x.packageOwnerId === this.user.id
            ).length
        },
        freeSpots() {
            const freeSpot = this.classItem.spots.filter((spot) => {
                return !spot.isOccupied && !spot.isInstructor && !spot.isBlocked
            })

            return freeSpot
        },
        isClassFull() {
            const freeSpot = this.classItem.spots.find((spot) => {
                return !spot.isOccupied && !spot.isInstructor && !spot.isBlocked
            })
            return !freeSpot
        },
        isBookable() {
            switch (this.bookingType) {
                case 'myself':
                    return this.isSelfBookable
                case 'family':
                    return this.isFamilyBookable
                case 'group':
                    return this.isGroupBookable
                default:
                    return false
            }
        },
        isWaitlistClass() {
            return !this.isBookable && this.isClassFull
        },
        isClassCancellable() {
            return this.isUserAuth && this.userBookedSpots?.length > 0
        },
        isWaitlistCancellable() {
            return this.isUserAuth && !!this.userWaitlist
        },
        allMemberships() {
            const memberships = []
            if (this.normalMemberships.length > 0) {
                memberships.push(
                    { header: this.$t('message.packages') },
                    ...this.normalMemberships
                )
            }
            if (this.recurringMemberships.length > 0) {
                memberships.push(
                    { header: this.$t('message.packagesRecurring') },
                    ...this.recurringMemberships
                )
            }
            return memberships
        },
        isFamilyBookingType() {
            return this.bookingType === 'family'
        },
        isGroupBookingType() {
            return this.bookingType === 'group'
        },
        classCredit() {
            return this.classItem.numCredits ?? 1
        },
        availableBookingTypes() {
            const availableBookingTypes = []
            availableBookingTypes.push('myself')
            if (this.classItem?.isFamilyBookingsEnabled)
                availableBookingTypes.push('family')

            if (this.classItem?.isGroupBookingsEnabled)
                availableBookingTypes.push('group')
            // }

            return availableBookingTypes
        },
        allAccounts() {
            return [this.mainAccount, ...this.subAccounts]
        },
    },
    methods: {
        async getSubAccounts() {
            if (!this.user || !this.user.id) {
                return
            }

            const response = await getSubAccountsByUser(this.user.id)
            if (response.success) {
                this.subAccounts = response.data.subAccounts ?? []
                this.mainAccount = response.data.user
            }
        },
        onChangeBookingType(bookingType) {
            this.bookingType = bookingType
        },
        updateSelectedSubAccounts(selectedSubAccounts) {
            this.selectedFamilyUsers = selectedSubAccounts
            this.$refs.ChooseSpotRef.resetSpotNumbers()
        },
        onExistingSignupFamily(users) {
            this.existingSignupFamilyUsers = users
        },
        async setRequiredDataWithLoading() {
            this.loading = true
            await this.setRequiredData()
            this.loading = false
        },
        async setRequiredData() {
            await this.setClasses() // this function needs to run before the other functions, so that
            if (!this.classExists) return
            const promises = [
                this.getSubAccounts(),
                this.setNormalPackages(),
                this.setRecurringPackages(),
                this.setUserPackages(),
                this.fetchUserBookedSpotsAndWaitlist(),
            ]

            await Promise.all(promises)
        },
        async setClasses() {
            this.classId = this.$route.params.classId
            this.loadButton = true // start loading button, stop loading after fetching booked spots / waitlist
            const result = await getClassByClassId({
                classId: this.classId,
            })
            if (result.success) {
                this.classItem = result.doc
                // Create an array of object here to make it scalable in the future
                // and to avoid any side effects.
                this.classItem.urls = this.classItem.images.map((image) => {
                    return { type: 'image', url: image }
                })

                if (this.classItem.videoUrl) {
                    this.classItem.urls.unshift({
                        type: 'video',
                        url: this.classItem.videoUrl,
                    })
                }
                this.isSelfBookable = result.isBookable
                this.reason = result.reason
                this.isFamilyBookable =
                    result.familyBookable && result.familyBookable.isBookable
                this.familyReason = result.familyBookable?.reason
                this.isGroupBookable =
                    result.groupBookable && result.groupBookable.isBookable
                this.groupReason = result.groupBookable?.reason
                this.classExists = true
            } else {
                this.error = result.error
                this.classExists = false
            }
        },
        filterUsablePackages(packages) {
            // This function helps to filter out packages which cannot be used to book this class
            return packages.filter((pkg) =>
                isPackageValidForClass(
                    pkg,
                    this.classItem,
                    this.studio.timezone
                )
            )
        },
        async setNormalPackages() {
            const result = await getNormalPackages(this.studio.id)
            if (result.success) {
                this.normalMemberships = this.filterUsablePackages(
                    result.memberships
                )
            }

            if (result.error) {
                this.error = result.error
            }
        },
        async setRecurringPackages() {
            const result = await getRecurringPackages(this.studio.id)
            if (result.success) {
                this.recurringMemberships = this.filterUsablePackages(
                    result.memberships
                )
            }

            if (result.error) {
                this.error = result.error
            }
        },
        async setUserPackages() {
            if (!this.isUserAuth) {
                this.myMemberships = []
                return
            }
            const result = await getMyMemberships(this.studio.id)
            if (result.success) {
                this.myMemberships = this.filterUsablePackages(
                    result.docs
                ).filter((doc) => doc.classesLeft > 0)
            }

            if (result.error) {
                this.error = result.error
            }
        },
        updateSpotNumbers(spotNumbers) {
            this.spotNumbers = spotNumbers
        },
        updateSelectedPackage(selectedPackage, isMyPackage) {
            this.selectedPackage = selectedPackage
            this.$gtag.event(SELECT_PACKAGE)
            this.isPurchasePackage = !isMyPackage
        },
        updateSelectedFamilyPackages(selectedPkgs) {
            this.selectedFamilyPackages = selectedPkgs
        },
        updateNumSpots(numSpots) {
            this.numSpots = numSpots
        },
        validate(isWaitlist) {
            if (!this.spotNumbers.length && !isWaitlist && !this.hideSpots) {
                this.validationError = this.$t(
                    'message.classSelectSpotBeforeBooking'
                )
                return false
            }

            if (this.isFamilyBookingType) {
                const hasSomeNotSelected =
                    !this.selectedFamilyPackages ||
                    this.selectedFamilyUsers.some(
                        (user) =>
                            !this.selectedFamilyPackages.find(
                                (pkg) => pkg.userId === user.id
                            )
                    )

                if (hasSomeNotSelected) {
                    this.validationError = this.$t(
                        'message.classSelectPackageBeforeBooking'
                    )
                    return false
                }
            } else if (!this.selectedPackage) {
                this.validationError = this.$t(
                    'message.classSelectPackageBeforeBooking'
                )
                return false
            }

            if (this.isFamilyBookingType) {
                const hasAnyLowCredit = this.selectedFamilyPackages.some(
                    (pkg) => pkg.classesLeft <= 0
                )
                if (hasAnyLowCredit) {
                    this.validationError = this.$t(
                        'message.classSelectValidPackage'
                    )
                    return false
                }
            } else if (this.selectedPackage.classesLeft <= 0) {
                this.validationError = this.$t(
                    'message.classSelectValidPackage'
                )
                return false
            }

            if (this.isFamilyBookingType) {
                if (this.hideSpots) {
                    const numSpotsPerUser =
                        this.numSpots / this.selectedFamilyUsers.length
                    if (
                        this.selectedFamilyPackages.some(
                            (pkg) => pkg.classesLeft < numSpotsPerUser
                        )
                    ) {
                        this.validationError = this.$t(
                            'message.classSelectValidPackageEnoughClasses'
                        )
                        return false
                    }
                } else {
                    const numSpotsPerUser =
                        this.spotNumbers.length /
                        this.selectedFamilyUsers.length
                    if (
                        this.selectedFamilyPackages.some(
                            (pkg) => pkg.classesLeft < numSpotsPerUser
                        )
                    ) {
                        this.validationError = this.$t(
                            'message.classSelectValidPackageEnoughClasses'
                        )
                        return false
                    }
                }
            } else if (this.hideSpots) {
                if (this.selectedPackage.classesLeft < this.numSpots) {
                    this.validationError = this.$t(
                        'message.classSelectValidPackageEnoughClasses'
                    )
                    return false
                }
            } else {
                if (
                    this.selectedPackage.classesLeft < this.spotNumbers.length
                ) {
                    this.validationError = this.$t(
                        'message.classSelectValidPackageEnoughClasses'
                    )
                    return false
                }
            }

            const spotsValid = this.spotNumbers || this.hideSpots

            if (
                spotsValid &&
                (this.selectedPackage || this.selectedFamilyPackages) &&
                !isWaitlist
            ) {
                this.validationError = ''
                return true
            }

            if (
                (this.selectedPackage || this.selectedFamilyPackages) &&
                isWaitlist
            ) {
                this.validationError = ''
                return true
            }
            return false
        },
        submit(isWaitlist) {
            const validation = this.validate(isWaitlist)
            if (validation) {
                this.$refs.CheckoutWindow.openDialog()
            }
        },
        async reload() {
            await this.setClasses()
            await this.setUserPackages()
            this.toggleKeys()
            await this.fetchUserBookedSpotsAndWaitlist()
        },
        toggleKeys() {
            this.chooseSpotKey = !this.chooseSpotKey
            this.bookSpotKey = !this.bookSpotKey
            this.checkoutWindowKey = !this.checkoutWindowKey
            this.selectFamilyPackageKey = !this.selectFamilyPackageKey
            this.selectUsersKey = !this.selectUsersKey
        },
        handleError(error) {
            this.validationError = error
        },
        async fetchUserBookedSpots() {
            this.loadButton = true
            this.loadingUserBookedSpots = true
            const doc = await getUserUpcomingClasses(this.studio.id, true)
            if (doc.success) {
                const result = doc.docs.filter(
                    (bookedClassItem) =>
                        bookedClassItem.classId === this.classItem.id
                )
                this.userBookedSpots = result
            }
            this.loadingUserBookedSpots = false
            this.loadButton = false
        },
        async fetchUserWaitlist() {
            this.loadButton = true
            this.loadingUserWaitlist = true
            const doc = await getUserWaitlist(this.studio.id)
            if (doc.success) {
                const result = doc.docs.filter(
                    (waitlistItem) => waitlistItem.classId === this.classItem.id
                )
                if (result.length > 0) {
                    this.userWaitlist = result[0] // should have only one waitlist for a class
                    this.waitlistPosition = this.userWaitlist.waitlistPosition
                }
            }
            this.loadingUserWaitlist = false
            this.loadButton = false
        },
        async fetchUserBookedSpotsAndWaitlist() {
            // reset userBookedSpots and userWaitlist
            this.userBookedSpots = []
            this.userWaitlist = null
            this.waitlistPosition = null
            if (this.isUserAuth)
                Promise.all([
                    this.fetchUserBookedSpots(),
                    this.fetchUserWaitlist(),
                ])
        },
        onSuccessCheckout() {
            this.isSuccessBooking = true
        },
    },
}
</script>

<style scoped>
.top-border {
    border-bottom: 0.2px solid #000;
}

.book-spot-header {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    /* font-size: 45px; */
    line-height: 32px;
    /* identical to box height, or 71% */

    display: flex;
    align-items: center;
}

.error-text {
    display: flex;
    justify-content: center;
    align-content: flex-start;
    align-items: flex-start;
    color: red;
}

.class-error {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
    align-items: center;
}

.class-error-text {
    font-family: Roboto;
    font-size: 24px;
    text-align: center;
}
</style>
